import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Search, Mail, CheckCircle, AlertCircle, Send, Users, Eye, X, TestTube, Settings, BookOpen, Image, FileText, Trash2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import Header from '@/components/Header';
import SendingProgressModal from '@/components/SendingProgressModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MonthlyMagazineEmail = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [searchParams, setSearchParams] = useState({
    magazine: 'Aabkari Times',
    recipient_type: 'both',
    status: 'ACTIVE'
  });
  
  // Sender accounts state
  const [senderAccounts, setSenderAccounts] = useState([]);
  const [selectedSender, setSelectedSender] = useState(null);
  
  // Progress modal state
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressJobId, setProgressJobId] = useState('');
  
  // Template settings
  const [template, setTemplate] = useState({
    subject: '',
    cover_image_url: '',
    read_now_link: '',
    pdf_link: '',
    // Editor's Letter mode fields
    send_style: 'newsletter',
    editor_name: '',
    intro_line: '',
    edition_label: ''
  });
  
  // Preview Modal State
  const [showPreview, setShowPreview] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  
  // Progress State (legacy - kept for compatibility)
  const [sendProgress, setSendProgress] = useState({ current: 0, total: 0, isActive: false });

  // Send Test Email function
  const handleSendTestEmail = async () => {
    if (!testEmail || !testEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    if (!template.subject) {
      toast.error('Please set the email subject first');
      return;
    }
    
    setSendingTest(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/email/send-test-magazine`, {
        magazine: searchParams.magazine,
        test_email: testEmail,
        subject: template.subject
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Test email sent to ${testEmail}! Check your inbox.`);
    } catch (error) {
      console.error('Test email error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  // Fetch sender accounts when magazine changes
  useEffect(() => {
    fetchSenderAccounts();
  }, [searchParams.magazine]);

  useEffect(() => {
    fetchTemplate();
  }, [searchParams.magazine]);

  const fetchSenderAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/email/sender-accounts/${searchParams.magazine}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSenderAccounts(response.data || []);
      
      // Set default sender if available
      const defaultSender = response.data?.find(a => a.is_default);
      if (defaultSender && !selectedSender) {
        setSelectedSender(defaultSender);
      } else if (response.data?.length > 0 && !selectedSender) {
        setSelectedSender(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching sender accounts:', error);
    }
  };

  const handleSenderChange = (accountId) => {
    const account = senderAccounts.find(a => a.id === accountId);
    if (account) {
      setSelectedSender(account);
    }
  };

  const fetchTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/magazine-templates/${searchParams.magazine}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplate({
        subject: response.data.subject || `${searchParams.magazine} - Latest Edition`,
        cover_image_url: response.data.cover_image_url || '',
        read_now_link: response.data.read_now_link || '',
        pdf_link: response.data.pdf_link || '',
        send_style: response.data.send_style || 'newsletter',
        editor_name: response.data.editor_name || '',
        intro_line: response.data.intro_line || '',
        edition_label: response.data.edition_label || ''
      });
    } catch (error) {
      console.error('Error fetching template:', error);
    }
  };

  const handleSaveTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const templateData = {
        magazine: searchParams.magazine,
        subject: template.subject || `${searchParams.magazine} - Latest Edition`,
        cover_image_url: template.cover_image_url || '',
        read_now_link: template.read_now_link || '',
        pdf_link: template.pdf_link || '',
        send_style: template.send_style || 'newsletter',
        editor_name: template.editor_name || '',
        intro_line: template.intro_line || '',
        edition_label: template.edition_label || ''
      };
      
      console.log('Saving template:', templateData);
      
      const response = await axios.post(`${API}/magazine-templates`, templateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Template save response:', response.data);
      toast.success('Template settings saved successfully!');
      setShowSettings(false);
    } catch (error) {
      console.error('Error saving template:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to save template settings');
    }
  };

  const fetchRecipients = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Use the combined members/advertisers search endpoint
      const response = await axios.post(`${API}/magazine-email/recipients`, searchParams, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecipients(response.data || []);
      setSelectedRecipients((response.data || []).map(r => r.id));
    } catch (error) {
      console.error('Error fetching recipients:', error);
      toast.error('Failed to fetch recipients');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectRecipient = (id, checked) => {
    if (checked) {
      setSelectedRecipients(prev => [...prev, id]);
    } else {
      setSelectedRecipients(prev => prev.filter(recipientId => recipientId !== id));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRecipients(recipients.map(r => r.id));
    } else {
      setSelectedRecipients([]);
    }
  };

  const allSelected = recipients.length > 0 && selectedRecipients.length === recipients.length;

  const handleOpenPreview = () => {
    if (!template.subject) {
      toast.error('Please set the email subject in Template Settings');
      return;
    }
    setShowPreview(true);
  };

  const handleCancelSend = () => {
    setShowPreview(false);
    setSending(false);
    setSendProgress({ current: 0, total: 0, isActive: false });
  };

  const handleConfirmSend = async () => {
    setSending(true);
    setShowPreview(false);
    const selectedRecipientData = recipients.filter(r => selectedRecipients.includes(r.id));
    
    try {
      const token = localStorage.getItem('token');
      
      // Start progress tracking
      const progressResponse = await axios.post(`${API}/email/progress/start`, {
        total: selectedRecipientData.length,
        email_type: 'monthly_magazine'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const jobId = progressResponse.data.job_id;
      setProgressJobId(jobId);
      setShowProgressModal(true);
      
      // Send all emails via batch endpoint
      const response = await axios.post(`${API}/magazine-email/send-batch`, {
        magazine: searchParams.magazine,
        recipients: selectedRecipientData,
        subject: template.subject,
        cover_image_url: template.cover_image_url,
        read_now_link: template.read_now_link,
        pdf_link: template.pdf_link,
        sender_email: selectedSender?.email || '',
        sender_name: selectedSender?.display_name || '',
        job_id: jobId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Successfully sent emails to ${response.data.sent_count} recipients!`);
    } catch (error) {
      console.error('Error sending emails:', error);
      toast.error('Failed to send some emails. Check reports for details.');
      setShowProgressModal(false);
    } finally {
      setSending(false);
    }
  };

  const getPreviewHtml = () => {
    const magazine = searchParams.magazine;
    const isAabkari = magazine === 'Aabkari Times' || magazine === 'Aabkari Times ENG';
    const logo = isAabkari 
      ? "https://share1.cloudhq-mkt3.net/f201fd9c7c9839.png"
      : "https://customer-assets.emergentagent.com/job_8737bc35-0275-4880-8379-60aad84a510d/artifacts/c7bu98qa_ST%20Logo%20%281%29.jpg";
    const website = isAabkari ? "https://www.aabkaritimes.com" : "https://www.sugartimes.co.in";
    const btnColor = isAabkari ? "#d32f2f" : "#166534";
    const readNowLink = template.read_now_link || website;
    const hasCover = !!template.cover_image_url;
    const hasPdf = !!template.pdf_link;
    
    const coverSection = hasCover 
      ? `<div style="flex: 1; text-align: center;">
           <img src="${template.cover_image_url}" alt="${magazine} - Latest Edition" style="max-width: 150px; height: auto; border: 1px solid #e5e7eb;">
         </div>` 
      : '';
    
    const pdfText = hasPdf
      ? `<p style="font-size: 11px; margin-top: 8px; color: #333; font-weight: bold;">PDF edition attached with this email.</p>
         <p style="margin-top: 6px;"><a href="${template.pdf_link}" style="display: inline-block; padding: 6px 12px; background: #333; color: #fff; text-decoration: none; border-radius: 4px; font-size: 11px; font-weight: bold;">Download PDF</a></p>`
      : `<p style="font-size: 11px; margin-top: 8px; color: #999;">No PDF link configured - set it in Template Settings.</p>`;
    
    return `
      <div style="background-color: #f0f0f0; padding: 15px;">
        <div style="max-width: 500px; margin: 0 auto; background: #fff; font-family: Arial, sans-serif; font-size: 11px;">
          <div style="padding: 15px; text-align: center;">
            <img src="${logo}" alt="${magazine}" style="max-width: 200px; height: auto;">
          </div>
          <div style="display: flex; padding: 10px;">
            ${coverSection}
            <div style="flex: 1; padding: 10px; text-align: left;">
              <h3 style="margin: 0 0 10px 0; font-size: 14px;">LATEST EDITION HIGHLIGHTS</h3>
              <p style="font-size: 11px; line-height: 1.4; color: #333;">
                Explore the latest developments in this month's edition of <strong>${magazine}</strong>.
              </p>
              <a href="${readNowLink}" style="display: inline-block; margin-top: 10px; padding: 8px 12px; background: ${btnColor}; color: #fff; text-decoration: none; border-radius: 4px; font-size: 12px;">Visit Website</a>
              ${pdfText}
            </div>
          </div>
          <div style="padding: 15px; background: #f9f9f9; text-align: center; font-size: 10px; color: #666;">
            <p>Bhawani Bhawan, 485 Mumfordganj, Opp. Shivaji Park, Prayagraj- 211002 (U.P.)</p>
          </div>
        </div>
      </div>
    `;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 py-8">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Button variant="outline" onClick={() => navigate('/')}>
              <ArrowLeft className="mr-2" size={18} />
              Back
            </Button>
            <h1 className="text-4xl font-bold" style={{ color: '#7c3aed' }}>
              Monthly Magazine Email
            </h1>
          </div>
          <Button 
            variant="outline" 
            onClick={() => setShowSettings(true)}
            className="border-purple-300"
          >
            <Settings className="mr-2" size={18} />
            Template Settings
          </Button>
        </div>

        {/* Current Template Info */}
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-blue-800">Current Template: {searchParams.magazine}</h3>
              <p className="text-sm text-blue-600">Subject: {template.subject || 'Not set'}</p>
              <p className="text-sm text-blue-600">
                Cover Image: {template.cover_image_url ? '✓ Uploaded' : 'Using placeholder'} | 
                PDF: {template.pdf_link ? '✓ Attached' : 'None'}
              </p>
            </div>
            <BookOpen className="text-blue-400" size={40} />
          </div>
        </div>

        {/* Progress Bar */}
        {sendProgress.isActive && (
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-blue-800">Sending Emails...</span>
              <span className="text-blue-600">{sendProgress.current} / {sendProgress.total}</span>
            </div>
            <div className="w-full bg-blue-200 rounded-full h-4">
              <div 
                className="bg-blue-600 h-4 rounded-full transition-all duration-300"
                style={{ width: `${(sendProgress.current / sendProgress.total) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Filter Section */}
        <div className="bg-white p-6 rounded-lg shadow-sm border mb-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <Search className="mr-2" size={20} />
            Filter Recipients
          </h2>
          <div className="grid grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Magazine</Label>
              <select
                value={searchParams.magazine}
                onChange={(e) => setSearchParams({ ...searchParams, magazine: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="Aabkari Times">Aabkari Times</option>
                <option value="Sugar Times">Sugar Times</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Recipient Type</Label>
              <select
                value={searchParams.recipient_type}
                onChange={(e) => setSearchParams({ ...searchParams, recipient_type: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="both">Members & Advertisers</option>
                <option value="members">Members Only</option>
                <option value="advertisers">Advertisers Only</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <select
                value={searchParams.status}
                onChange={(e) => setSearchParams({ ...searchParams, status: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="ACTIVE">Active Only</option>
                <option value="ALL">All (Including Inactive)</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>&nbsp;</Label>
              <Button 
                onClick={fetchRecipients} 
                className="w-full"
                disabled={loading}
                style={{ backgroundColor: '#7c3aed' }}
              >
                <Search className="mr-2" size={18} />
                {loading ? 'Searching...' : 'Search Recipients'}
              </Button>
            </div>
          </div>
          
          {/* Sender Email Selection */}
          <div className="mt-4 bg-indigo-50 p-4 rounded-lg border border-indigo-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Label className="font-semibold text-indigo-800">Send From:</Label>
                <select
                  value={selectedSender?.id || ''}
                  onChange={(e) => handleSenderChange(e.target.value)}
                  className="h-10 w-72 rounded-md border border-indigo-300 bg-white px-3 py-2 text-sm"
                >
                  {senderAccounts.length === 0 ? (
                    <option value="">No sender accounts configured</option>
                  ) : (
                    senderAccounts.map(account => (
                      <option key={account.id} value={account.id}>
                        {account.display_name} ({account.email})
                      </option>
                    ))
                  )}
                </select>
              </div>
              {senderAccounts.length === 0 && (
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => navigate('/email-settings')}
                  className="text-indigo-700 border-indigo-300"
                >
                  <Settings className="mr-1" size={14} />
                  Configure
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Stats */}
        {recipients.length > 0 && (
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-gray-800">{recipients.length}</p>
                  <p className="text-sm text-gray-600">Total Recipients</p>
                </div>
                <Users className="text-gray-400" size={32} />
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-blue-600">{recipients.filter(r => r.type === 'Member').length}</p>
                  <p className="text-sm text-gray-600">Members</p>
                </div>
                <Users className="text-blue-400" size={32} />
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-purple-600">{recipients.filter(r => r.type === 'Advertiser').length}</p>
                  <p className="text-sm text-gray-600">Advertisers</p>
                </div>
                <Users className="text-purple-400" size={32} />
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-purple-800">{selectedRecipients.length}</p>
                  <p className="text-sm text-purple-600">Selected for Email</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Bar */}
        {recipients.length > 0 && (
          <div className="bg-gray-100 p-4 rounded-lg mb-6 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Checkbox
                checked={allSelected}
                onCheckedChange={handleSelectAll}
                id="select-all"
              />
              <label htmlFor="select-all" className="text-sm font-medium cursor-pointer">
                Select All ({recipients.length})
              </label>
            </div>
            
            <Button 
              onClick={handleOpenPreview} 
              disabled={sending || selectedRecipients.length === 0}
              style={{ backgroundColor: '#059669' }}
            >
              <Eye className="mr-2" size={18} />
              Preview & Send ({selectedRecipients.length})
            </Button>
          </div>
        )}

        {/* Recipients Table */}
        {recipients.length > 0 && (
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="p-3 text-left w-12"></th>
                    <th className="p-3 text-left">ID</th>
                    <th className="p-3 text-left">Name</th>
                    <th className="p-3 text-left">Company</th>
                    <th className="p-3 text-left">Email</th>
                    <th className="p-3 text-left">Type</th>
                    <th className="p-3 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recipients.map((recipient) => (
                    <tr key={recipient.id} className="border-b hover:bg-gray-50">
                      <td className="p-3">
                        <Checkbox
                          checked={selectedRecipients.includes(recipient.id)}
                          onCheckedChange={(checked) => handleSelectRecipient(recipient.id, checked)}
                        />
                      </td>
                      <td className="p-3 font-medium">{recipient.id}</td>
                      <td className="p-3">{recipient.name}</td>
                      <td className="p-3">{recipient.company}</td>
                      <td className="p-3 text-green-600">{recipient.email}</td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium text-white ${recipient.type === 'Member' ? 'bg-blue-500' : 'bg-purple-500'}`}>
                          {recipient.type}
                        </span>
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${recipient.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                          {recipient.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && recipients.length === 0 && (
          <div className="bg-white p-12 rounded-lg shadow-sm border text-center">
            <BookOpen className="mx-auto text-gray-400 mb-4" size={64} />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No Recipients Found</h3>
            <p className="text-gray-500">
              Use the filters above to search for members and advertisers to send the monthly magazine.
            </p>
          </div>
        )}

        {/* Preview Modal */}
        {showPreview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
              <div className="bg-purple-600 text-white p-4 flex items-center justify-between">
                <h2 className="text-xl font-bold flex items-center">
                  <Eye className="mr-2" size={24} />
                  Email Preview - Monthly Magazine
                </h2>
                <button onClick={handleCancelSend} className="text-white hover:text-gray-200">
                  <X size={24} />
                </button>
              </div>
              
              <div className="p-6 overflow-auto" style={{ maxHeight: 'calc(90vh - 180px)' }}>
                <div className="mb-4 p-4 bg-gray-50 rounded-lg border">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <strong>Magazine:</strong> {searchParams.magazine}
                    </div>
                    <div>
                      <strong>Recipients:</strong> {selectedRecipients.length}
                    </div>
                    <div className="col-span-2">
                      <strong>Subject:</strong> {template.subject}
                    </div>
                  </div>
                </div>
                
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-100 p-3 border-b">
                    <p className="text-sm font-medium text-gray-600">Email Preview</p>
                  </div>
                  <div 
                    className="p-0 bg-white"
                    dangerouslySetInnerHTML={{ __html: getPreviewHtml() }}
                  />
                </div>
              </div>
              
              <div className="bg-gray-100 p-4 flex justify-between items-center border-t">
                <Button variant="outline" onClick={handleCancelSend} className="text-gray-600">
                  <X className="mr-2" size={18} />
                  Cancel
                </Button>
                <div className="flex space-x-3">
                  <p className="text-sm text-gray-500 self-center">
                    Ready to send to <strong>{selectedRecipients.length}</strong> recipients?
                  </p>
                  <Button 
                    onClick={handleConfirmSend}
                    disabled={sending}
                    style={{ backgroundColor: '#059669' }}
                  >
                    <Send className="mr-2" size={18} />
                    {sending ? 'Sending...' : `Confirm & Send (${selectedRecipients.length})`}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Settings Modal with File Upload */}
        {showSettings && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
              <div className="bg-purple-600 text-white p-4 flex items-center justify-between">
                <h2 className="text-xl font-bold flex items-center">
                  <Settings className="mr-2" size={24} />
                  Template Settings - {searchParams.magazine}
                </h2>
                <button onClick={() => setShowSettings(false)} className="text-white hover:text-gray-200">
                  <X size={24} />
                </button>
              </div>
              
              <div className="p-6 space-y-6 overflow-auto" style={{ maxHeight: 'calc(90vh - 140px)' }}>
                <div className="space-y-2">
                  <Label>Email Subject</Label>
                  <Input 
                    value={template.subject}
                    onChange={(e) => setTemplate({ ...template, subject: e.target.value })}
                    placeholder={`${searchParams.magazine} - December 2025 Edition`}
                  />
                  <p className="text-xs text-gray-500">Change this every month (e.g., "Aabkari Times - January 2025 Edition")</p>
                </div>

                {/* SEND STYLE — Newsletter vs Editor's Letter */}
                <div className="space-y-2 p-3 bg-indigo-50 border border-indigo-200 rounded">
                  <Label className="flex items-center font-semibold text-indigo-900">
                    <Mail className="mr-2" size={16} />
                    Send Style
                  </Label>
                  <select
                    value={template.send_style || 'newsletter'}
                    onChange={(e) => setTemplate({ ...template, send_style: e.target.value })}
                    className="w-full px-3 py-2 border rounded bg-white"
                    data-testid="send-style-select"
                  >
                    <option value="newsletter">Newsletter Mode (full HTML, lands in Promotions)</option>
                    <option value="editor_letter">Editor's Letter Mode (plain, lands in Primary inbox)</option>
                  </select>
                  <p className="text-xs text-gray-600">
                    <strong>Editor's Letter</strong> = minimal HTML, single "Download PDF" button, no extra links. Looks like a 1-on-1 email — much better chance of landing in Primary inbox.
                  </p>
                </div>

                {/* Editor's Letter mode-specific fields */}
                {template.send_style === 'editor_letter' && (
                  <div className="space-y-3 p-3 bg-amber-50 border border-amber-200 rounded">
                    <div className="space-y-2">
                      <Label>Edition Label (small line above greeting)</Label>
                      <Input
                        value={template.edition_label}
                        onChange={(e) => setTemplate({ ...template, edition_label: e.target.value })}
                        placeholder="December 2025 Edition"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Intro Line (1-2 sentences from the editor)</Label>
                      <Input
                        value={template.intro_line}
                        onChange={(e) => setTemplate({ ...template, intro_line: e.target.value })}
                        placeholder="Sharing this month's edition — covers excise policy updates and our annual review."
                      />
                      <p className="text-xs text-gray-500">Keep it short and personal. Leave blank for default.</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Editor's Name (signoff)</Label>
                      <Input
                        value={template.editor_name}
                        onChange={(e) => setTemplate({ ...template, editor_name: e.target.value })}
                        placeholder={`Editor, ${searchParams.magazine}`}
                      />
                    </div>
                  </div>
                )}

                {/* Cover Image URL */}
                <div className="space-y-2">
                  <Label className="flex items-center">
                    <Image className="mr-2" size={16} />
                    Magazine Cover Image (URL or Google Drive share link)
                  </Label>
                  <Input
                    value={template.cover_image_url}
                    onChange={(e) => setTemplate({ ...template, cover_image_url: e.target.value })}
                    placeholder="https://drive.google.com/file/d/XXXXX/view?usp=sharing"
                  />
                  <p className="text-xs text-gray-500">
                    📁 <strong>Google Drive link works</strong>: just paste the share URL — it'll be auto-converted to a direct image URL. Make sure Drive file is set to "<strong>Anyone with the link can view</strong>".
                  </p>
                  {template.cover_image_url && (
                    <div className="flex items-center space-x-3 mt-2">
                      <img src={template.cover_image_url} alt="Cover Preview" className="h-24 rounded shadow border" 
                        onError={(e) => { e.target.style.display = 'none'; }} />
                      <Button variant="outline" size="sm" onClick={() => setTemplate({ ...template, cover_image_url: '' })} className="text-red-600">
                        <Trash2 className="mr-1" size={14} /> Clear
                      </Button>
                    </div>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label>"Visit Website" Button Link</Label>
                  <Input 
                    value={template.read_now_link}
                    onChange={(e) => setTemplate({ ...template, read_now_link: e.target.value })}
                    placeholder={`https://${searchParams.magazine === 'Aabkari Times' ? 'aabkaritimes.com' : 'sugartimes.co.in'}/latest-edition/`}
                  />
                  <p className="text-xs text-gray-500">Link to the specific edition page on your website</p>
                </div>
                
                {/* PDF Link URL */}
                <div className="space-y-2">
                  <Label className="flex items-center">
                    <FileText className="mr-2" size={16} />
                    Magazine PDF Link (URL or Google Drive share link)
                  </Label>
                  <Input 
                    value={template.pdf_link}
                    onChange={(e) => setTemplate({ ...template, pdf_link: e.target.value })}
                    placeholder="https://drive.google.com/file/d/XXXXX/view?usp=sharing"
                  />
                  <p className="text-xs text-gray-500">
                    📁 <strong>Google Drive link works</strong> — perfect for large PDFs (70-150MB). Just paste the share URL and it'll be auto-converted to a "Download PDF" button. Make sure the Drive file is set to "<strong>Anyone with the link can view</strong>".
                  </p>
                  {template.pdf_link && (
                    <div className="flex items-center space-x-2 mt-1">
                      <FileText className="text-red-500" size={16} />
                      <span className="text-sm text-gray-700 truncate max-w-xs">{template.pdf_link.split('/').pop()}</span>
                      <Button variant="outline" size="sm" onClick={() => setTemplate({ ...template, pdf_link: '' })} className="text-red-600 ml-auto">
                        <Trash2 className="mr-1" size={14} /> Clear
                      </Button>
                    </div>
                  )}
                </div>
                
                {/* Test Email Section */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 space-y-3">
                  <Label className="flex items-center text-yellow-800 font-semibold">
                    <TestTube className="mr-2" size={18} />
                    Send Test Email
                  </Label>
                  <p className="text-xs text-yellow-700">
                    Test the email on your own inbox before sending to all recipients.
                  </p>
                  <div className="flex space-x-2">
                    <Input 
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="your-email@example.com"
                      type="email"
                      className="flex-1"
                    />
                    <Button 
                      onClick={handleSendTestEmail}
                      disabled={sendingTest || !template.subject}
                      className="bg-yellow-600 hover:bg-yellow-700 text-white"
                    >
                      {sendingTest ? (
                        <>
                          <Loader2 className="mr-2 animate-spin" size={16} />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="mr-2" size={16} />
                          Send Test
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-yellow-600">
                    Test email will be sent with [TEST] prefix in subject line.
                  </p>
                </div>
              </div>
              
              <div className="bg-gray-100 p-4 flex justify-end space-x-3 border-t">
                <Button variant="outline" onClick={() => setShowSettings(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSaveTemplate}
                  style={{ backgroundColor: '#7c3aed' }}
                >
                  <CheckCircle className="mr-2" size={18} />
                  Save Template
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {/* Sending Progress Modal */}
        <SendingProgressModal
          isOpen={showProgressModal}
          onClose={() => setShowProgressModal(false)}
          jobId={progressJobId}
          totalRecipients={selectedRecipients.length}
          emailType="Monthly Magazine"
        />
      </div>
    </div>
  );
};

export default MonthlyMagazineEmail;
